import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Play,
    Star,
    Calendar,
    Clock,
    ChevronDown,
    Info,
    ArrowLeft,
    X,
    ChevronLeft,
    ChevronRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { fetchSeriesDetails, fetchSeasonDetails } from '@/services/tmdb';

const SeriesDetailsPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [series, setSeries] = useState(null);
    const [selectedSeason, setSelectedSeason] = useState(null);
    const [episodes, setEpisodes] = useState([]);
    const [isLoadingSeries, setIsLoadingSeries] = useState(true);
    const [isLoadingSeason, setIsLoadingSeason] = useState(false);
    const [playingEpisode, setPlayingEpisode] = useState(null);

    // Fetch Series Details
    useEffect(() => {
        const loadSeries = async () => {
            setIsLoadingSeries(true);
            try {
                const data = await fetchSeriesDetails(id);
                setSeries(data);

                // Select first season by default (usually season 1, or 0 if specials exist and are preferred)
                if (data.seasons && data.seasons.length > 0) {
                    // Filter out season 0 (Specials) for default selection unless it's the only one
                    const defaultSeason = data.seasons.find(s => s.season_number === 1) || data.seasons[0];
                    setSelectedSeason(defaultSeason);
                }
            } catch (error) {
                console.error("Error fetching series details:", error);
            } finally {
                setIsLoadingSeries(false);
            }
        };

        if (id) {
            loadSeries();
        }
    }, [id]);

    // Fetch Season Details when selected season changes
    useEffect(() => {
        const loadEpisodes = async () => {
            if (!selectedSeason || !series) return;

            setIsLoadingSeason(true);
            try {
                const data = await fetchSeasonDetails(series.id, selectedSeason.season_number);
                setEpisodes(data.episodes);
            } catch (error) {
                console.error("Error fetching season details:", error);
            } finally {
                setIsLoadingSeason(false);
            }
        };

        loadEpisodes();
    }, [selectedSeason, series]);

    const handleSeasonChange = (season) => {
        setSelectedSeason(season);
    };

    const handleEpisodeClick = (episode) => {
        if (episode.customSource) {
            setPlayingEpisode(episode);
        } else {
            // Optional: fallback logic, e.g. toast("Episode not available yet")
            console.log("No custom source for this episode");
        }
    };

    const handleNextEpisode = () => {
        if (!playingEpisode) return;
        const currentIndex = episodes.findIndex(e => e.id === playingEpisode.id);
        if (currentIndex < episodes.length - 1) {
            setPlayingEpisode(episodes[currentIndex + 1]);
        }
    };

    const handlePrevEpisode = () => {
        if (!playingEpisode) return;
        const currentIndex = episodes.findIndex(e => e.id === playingEpisode.id);
        if (currentIndex > 0) {
            setPlayingEpisode(episodes[currentIndex - 1]);
        }
    };

    if (isLoadingSeries) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-background">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (!series) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-background text-foreground">
                <h1 className="text-2xl font-bold mb-4">Series no encontrada</h1>
                <Button onClick={() => navigate('/series')}>Volver a Series</Button>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background pb-20">
            {/* Hero Section */}
            <div className="relative w-full h-[60vh] md:h-[75vh] lg:h-[85vh]">
                <div className="absolute inset-0">
                    <img
                        src={series.backdrop || series.poster}
                        alt={series.title}
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
                    <div className="absolute inset-0 bg-gradient-to-r from-background via-background/60 to-transparent" />
                </div>

                <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-12 lg:p-20">
                    <Button
                        variant="ghost"
                        onClick={() => navigate('/series')}
                        className="absolute top-24 left-6 md:left-12 text-white/80 hover:text-white hover:bg-white/10 w-fit mb-4 group"
                    >
                        <ArrowLeft className="mr-2 h-4 w-4 transition-transform group-hover:-translate-x-1" /> Volver
                    </Button>

                    <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-4 text-shadow-xl tracking-tight">
                        {series.title}
                    </h1>

                    <div className="flex flex-wrap items-center gap-4 text-sm md:text-base text-gray-200 mb-6 font-medium">
                        <div className="flex items-center gap-1 text-yellow-400 bg-yellow-400/10 px-2 py-0.5 rounded backdrop-blur-sm border border-yellow-400/20">
                            <Star className="w-4 h-4 fill-current" />
                            <span>{series.rating || 'NR'}</span>
                        </div>
                        <span className="text-white/40">|</span>
                        <span>{series.year || 'N/A'}</span>
                        {series.numberOfSeasons ? (
                            <>
                                <span className="text-white/40">|</span>
                                <span>{series.numberOfSeasons} Temporadas</span>
                            </>
                        ) : null}
                        <span className="text-white/40">|</span>
                        <div className="flex gap-2">
                            {series.genres?.slice(0, 3).map((g, i) => (
                                <span key={i} className="px-2 py-0.5 bg-white/10 backdrop-blur-md border border-white/20 rounded text-xs text-white/90">
                                    {g}
                                </span>
                            ))}
                        </div>
                    </div>

                    <p className="text-gray-300/90 max-w-3xl text-lg md:text-xl mb-8 line-clamp-3 md:line-clamp-4 leading-relaxed font-light">
                        {series.description}
                    </p>
                </div>
            </div>

            {/* Video Player Overlay */}
            {playingEpisode && (
                <div className="fixed inset-0 z-50 bg-black/95 flex flex-col justify-center items-center p-4">
                    <div className="relative w-full max-w-6xl aspect-video bg-black border border-white/10 shadow-2xl rounded-xl overflow-hidden">
                        <iframe
                            src={playingEpisode.customSource}
                            className="w-full h-full"
                            frameBorder="0"
                            allowFullScreen
                            sandbox="allow-forms allow-scripts allow-same-origin allow-presentation"
                            referrerPolicy="no-referrer"
                            allow="autoplay; encrypted-media"
                            title={playingEpisode.name}
                        ></iframe>
                        <button
                            onClick={() => setPlayingEpisode(null)}
                            className="absolute top-4 right-4 p-2 bg-black/50 text-white rounded-full hover:bg-red-600 transition-colors backdrop-blur-md"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>
                    <div className="mt-4 w-full max-w-6xl flex items-center justify-between gap-4 text-white">
                        <Button
                            variant="outline"
                            onClick={handlePrevEpisode}
                            disabled={!playingEpisode || episodes.findIndex(e => e.id === playingEpisode.id) <= 0}
                            className="bg-white/10 border-white/20 hover:bg-white/20 text-white disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                            <ChevronLeft className="mr-2 h-4 w-4" /> Anterior
                        </Button>

                        <div className="text-center">
                            <h2 className="text-xl md:text-2xl font-bold">{series.title}</h2>
                            <p className="text-base md:text-lg text-gray-400">
                                S{selectedSeason.season_number} E{playingEpisode.episodeNumber}: {playingEpisode.name}
                            </p>
                        </div>

                        <Button
                            variant="outline"
                            onClick={handleNextEpisode}
                            disabled={!playingEpisode || episodes.findIndex(e => e.id === playingEpisode.id) >= episodes.length - 1}
                            className="bg-white/10 border-white/20 hover:bg-white/20 text-white disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                            Siguiente <ChevronRight className="ml-2 h-4 w-4" />
                        </Button>
                    </div>
                </div>
            )}

            {/* Content Section */}
            <div className="max-w-[1920px] mx-auto px-6 md:px-12 lg:px-20 -mt-16 relative z-10">
                {/* Season Selector Toolbar */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8 bg-card/80 backdrop-blur-xl border border-white/10 p-4 rounded-xl shadow-2xl">
                    <div className="flex items-center gap-3">
                        <div className="w-1 h-8 bg-primary rounded-full hidden md:block" />
                        <h2 className="text-2xl font-bold text-white">Episodios</h2>
                        <span className="text-muted-foreground text-sm font-medium mt-1">
                            {selectedSeason ? selectedSeason.name : ''}
                        </span>
                    </div>

                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="outline"
                                className="w-full md:w-[240px] justify-between bg-black/40 text-white border-white/10 hover:bg-white/10 hover:text-white backdrop-blur-md"
                            >
                                <span className="font-medium truncate mr-2">
                                    {selectedSeason ? (selectedSeason.name || `Temporada ${selectedSeason.season_number}`) : 'Seleccionar Temporada'}
                                </span>
                                <ChevronDown className="ml-auto h-4 w-4 opacity-70" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                            align="end"
                            className="w-[240px] max-h-[400px] overflow-y-auto bg-black/95 border-white/10 backdrop-blur-xl text-white shadow-xl z-50"
                        >
                            {series.seasons && series.seasons.length > 0 ? (
                                series.seasons
                                    .filter(s => s && typeof s.season_number === 'number')
                                    .map((season, index) => (
                                        <DropdownMenuItem
                                            key={`season-${season.id}-${index}`}
                                            onClick={() => handleSeasonChange(season)}
                                            className={cn(
                                                "cursor-pointer py-3 px-3 focus:bg-white/10 focus:text-white",
                                                selectedSeason?.season_number === season.season_number && "bg-primary/20 text-primary focus:bg-primary/30 focus:text-primary"
                                            )}
                                        >
                                            <div className="flex flex-col gap-1 w-full">
                                                <span className="font-medium text-sm">
                                                    {season.name || `Temporada ${season.season_number}`}
                                                </span>
                                                <span className="text-[10px] text-white/60">
                                                    {(season.episode_count || 0)} episodios
                                                </span>
                                            </div>
                                        </DropdownMenuItem>
                                    ))
                            ) : (
                                <div className="p-3 text-center text-sm text-muted-foreground">
                                    No hay temporadas disponibles
                                </div>
                            )}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>

                {/* Episodes Grid */}
                {isLoadingSeason ? (
                    <div className="flex flex-col items-center justify-center py-32 opacity-80">
                        {/* <Loader2 className="w-10 h-10 text-primary animate-spin mb-4" /> */}
                        <p className="text-muted-foreground animate-pulse">Cargando episodios...</p>
                    </div>
                ) : episodes && episodes.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 pb-20">
                        {episodes.map((episode) => (
                            <div
                                key={episode.id}
                                onClick={() => handleEpisodeClick(episode)}
                                className={cn(
                                    "group relative bg-card/50 rounded-xl overflow-hidden border border-white/5 transition-all duration-300 hover:shadow-2xl hover:shadow-primary/10 hover:-translate-y-1 cursor-pointer",
                                    "hover:border-primary/50" // Always full color, hover border prompt
                                )}
                            >
                                {/* Thumbnail */}
                                <div className="aspect-video w-full relative overflow-hidden bg-black/40">
                                    <img
                                        src={episode.stillPath || series.backdrop || series.poster}
                                        alt={episode.name}
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                        loading="lazy"
                                    />

                                    <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors duration-300" />

                                    {/* Play Overlay */}
                                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                                        <div className="w-14 h-14 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center group-hover:scale-110 transition-transform duration-300 hover:bg-primary hover:border-primary">
                                            <Play className="w-6 h-6 fill-white text-white ml-1" />
                                        </div>
                                    </div>

                                    <div className="absolute top-2 right-2 px-2 py-1 bg-black/60 backdrop-blur-md rounded text-[10px] font-bold text-white/90 border border-white/10">
                                        EP {episode.episodeNumber}
                                    </div>

                                    {episode.runtime && (
                                        <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/60 backdrop-blur-md rounded text-[10px] font-medium text-white/90 flex items-center gap-1 border border-white/10">
                                            <Clock className="w-3 h-3" />
                                            {episode.runtime}m
                                        </div>
                                    )}
                                </div>

                                {/* Info */}
                                <div className="p-5">
                                    <div className="flex justify-between items-start mb-2 gap-2">
                                        <h3 className="font-bold text-base text-foreground line-clamp-1 group-hover:text-primary transition-colors" title={episode.name}>
                                            {episode.name}
                                        </h3>
                                    </div>

                                    <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3 font-medium">
                                        <span className="flex items-center gap-1">
                                            <Calendar className="w-3 h-3" />
                                            {episode.airDate ? new Date(episode.airDate).toLocaleDateString() : 'N/A'}
                                        </span>
                                        {episode.voteAverage > 0 && (
                                            <>
                                                <span className="w-1 h-1 rounded-full bg-white/20" />
                                                <span className="flex items-center gap-1 text-yellow-500/90">
                                                    <Star className="w-3 h-3 fill-current" />
                                                    {episode.voteAverage.toFixed(1)}
                                                </span>
                                            </>
                                        )}
                                    </div>

                                    <p className="text-sm text-muted-foreground/80 line-clamp-3 leading-relaxed">
                                        {episode.overview || "Sin descripción disponible para este episodio."}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center py-20 text-center min-h-[300px] border border-dashed border-white/10 rounded-xl bg-white/5">
                        <Info className="w-10 h-10 text-muted-foreground mb-4 opacity-50" />
                        <h3 className="text-xl font-medium text-foreground mb-2">No hay episodios disponibles</h3>
                        <p className="text-muted-foreground max-w-md">
                            No se encontraron episodios para esta temporada. Intenta seleccionar otra.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SeriesDetailsPage;
